function initScrollSnapBanners(sectionId) {
  const scrollContainer = document.getElementById(`scrollSnapContainer-${sectionId}`);
  const anchorMenu = document.getElementById(`scrollSnapAnchorMenu-${sectionId}`);
  const sideIndicator = document.getElementById(`scrollSnapSideIndicator-${sectionId}`);

  if (!scrollContainer) return;

  let ticking = false;

  // Debounced wheel handler for better performance
  function handleWheel(e) {
    if (!ticking) {
      requestAnimationFrame(() => {
        const rect = scrollContainer.getBoundingClientRect();
        const isFooterVisible = rect.bottom < window.innerHeight - 1;

        if (e.deltaY < 0 && isFooterVisible) {
          e.preventDefault();
          const scrollAmount = e.deltaY * 5;
          window.scrollBy(0, scrollAmount);
        }
        ticking = false;
      });
      ticking = true;
    }
  }

  scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

  // Initialize carousels with lazy loading
  const carousels = scrollContainer.querySelectorAll('.banner-carousel-container');
  carousels.forEach(carousel => {
    const track = carousel.querySelector('.banner-carousel-track');
    const slides = carousel.querySelectorAll('.banner-carousel-slide');
    const dots = carousel.querySelectorAll('.banner-carousel-dots button');
    
    if (slides.length <= 1) return;
    
    let currentSlide = 0;
    let autoplayInterval;
    let autoplayDisabled = false;
    
    function updateCarousel() {
      track.style.transform = `translateX(-${currentSlide * 100}%)`;
      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentSlide);
      });
    }
    
    function nextSlide() {
      currentSlide = (currentSlide + 1) % slides.length;
      updateCarousel();
    }
    
    function startAutoplay() {
      if (!autoplayDisabled) {
        autoplayInterval = setInterval(nextSlide, 6000);
      }
    }
    
    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
      }
    }
    
    // Event delegation for better performance
    carousel.addEventListener('click', (e) => {
      const dot = e.target.closest('.banner-carousel-dots button');
      if (dot) {
        const index = Array.from(dots).indexOf(dot);
        if (index !== -1) {
          currentSlide = index;
          updateCarousel();
          stopAutoplay();
          autoplayDisabled = true;
        }
      }
    });
    
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);
    
    startAutoplay();
  });

  // Anchor Menu with cached elements
  let anchorButtons;
  if (anchorMenu) {
    anchorButtons = anchorMenu.querySelectorAll('button[data-target]');
    // Event delegation for anchor menu
    anchorMenu.addEventListener('click', (e) => {
      const button = e.target.closest('button[data-target]');
      if (button) {
        const targetId = button.dataset.target;
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  // The media attribute on <source> is only evaluated when the video first
  // loads, so swap the file manually when the viewport crosses a breakpoint
  // (e.g. tablet rotation or a resized desktop window).
  const backgroundVideos = scrollContainer.querySelectorAll('video.banner-background-video');
  backgroundVideos.forEach(video => {
    const sources = Array.from(video.querySelectorAll('source'));
    if (sources.length < 2) return;

    // Mirrors the browser's own selection: first source whose media query
    // matches, with a media-less source acting as the fallback.
    function pickSourceUrl() {
      const source = sources.find(s => !s.media || window.matchMedia(s.media).matches);
      return source ? source.getAttribute('src') : null;
    }

    function applySource() {
      const newSrc = pickSourceUrl();
      if (!newSrc || video.currentSrc === new URL(newSrc, location.href).href) return;

      const wasPlaying = !video.paused;
      video.src = newSrc;
      video.load();
      if (wasPlaying) {
        video.play().catch(() => {
          // Autoplay restrictions; the intersection observer will retry.
        });
      }
    }

    const mediaQueries = [...new Set(sources.map(s => s.media).filter(Boolean))];
    mediaQueries.forEach(query => {
      window.matchMedia(query).addEventListener('change', applySource);
    });
  });

  // Cache frequently accessed elements
  const bannerItems = scrollContainer.querySelectorAll('.banner-item');
  const totalBanners = bannerItems.length;
  const sideButtons = sideIndicator ? sideIndicator.querySelectorAll('button') : [];

  // Optimized intersection observer with video handling
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const banner = entry.target;
      const videos = banner.querySelectorAll('video');
      
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        const targetId = `#${entry.target.id}`;
        
        // Find current banner index more efficiently
        const currentBannerIndex = Array.from(bannerItems).findIndex(item => 
          item.id === entry.target.id
        );
        
        if (currentBannerIndex === -1) return;
        
        // Play videos when banner comes into view
        videos.forEach(video => {
          if (video.paused) {
            video.play().catch(e => {
              // Handle autoplay restrictions gracefully
              console.log('Video autoplay prevented:', e);
            });
          }
        });
        
        // Update anchor menu
        if (anchorMenu && anchorButtons) {
          const correspondingAnchorButton = anchorMenu.querySelector(`button[data-target="${targetId}"]`);
          if (correspondingAnchorButton) {
            // Remove active class from all buttons efficiently
            anchorButtons.forEach(btn => btn.classList.remove('active'));
            correspondingAnchorButton.classList.add('active');
          }
        }
        
        // Update side indicator
        if (sideButtons.length > 0) {
          sideButtons.forEach((button, index) => {
            if (index < currentBannerIndex) {
              button.className = 'completed';
            } else if (index === currentBannerIndex) {
              button.className = 'active';
            } else {
              button.className = '';
            }
          });
        }
      } else {
        // Pause videos when banner goes out of view to save bandwidth
        videos.forEach(video => {
          if (!video.paused) {
            video.pause();
          }
        });
      }
    });
  }, { root: scrollContainer, threshold: 0.5 });

  // Observe all banner items
  bannerItems.forEach(section => {
    observer.observe(section);
  });

  // Lookahead loader: non-first videos render with preload="none", and the
  // play observer above only fires at 50% visibility, which with an instant
  // scroll snap means a blank frame while the video fetches. Start loading
  // once a banner is within one container-height of view so it's ready by
  // the time the snap lands.
  const lookaheadObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      entry.target.querySelectorAll('video').forEach(video => {
        // Only videos still deferred; skips the autoplaying first banner so
        // load() never resets playback.
        if (video.preload === 'none') {
          video.preload = 'auto';
          video.load();
        }
      });

      // One-shot per banner: once loading has started there's nothing to redo.
      lookaheadObserver.unobserve(entry.target);
    });
  }, { root: scrollContainer, rootMargin: '100% 0px' });

  bannerItems.forEach(section => {
    const hasDeferredVideo = section.querySelector('video[preload="none"]');
    if (hasDeferredVideo) {
      lookaheadObserver.observe(section);
    }
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize sections passed from templates
  if (window.scrollSnapSections && window.scrollSnapSections.length > 0) {
    window.scrollSnapSections.forEach(sectionId => {
      initScrollSnapBanners(sectionId);
    });
  }
});
